import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import IpeConfirmDialog from '@/components/ipe/IpeConfirmDialog';
import SheinSyncProgressBar from '@/shein-platform/components/SheinSyncProgressBar';
import SheinOpsPager, {
  SHEIN_OPS_DEFAULT_PAGE_SIZE,
} from '@/shein-platform/components/SheinOpsPager';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import {
  ProductFilterCard,
  ProductListCard,
} from '@/shein-platform/modules/products/components/ProductPageCards';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import {
  buildSheinPath,
  normalizeSheinShopTypeSlug,
} from '@/shein-platform/constants/sheinRoutes';
import {
  activityTypeLabel,
  adjustFormLabel,
  enrollProductInfo,
  formatPrice,
  formatTimeRange,
  groupEnrollRowsBySpu,
  partakeFieldLabels,
  partakeScopeLabel,
} from '@/shein-platform/utils/sheinMarketingDisplay';
import {
  buildSheinCampaignGoodsInfoList,
  defaultDraftFromProduct,
  extractSkcPriceInfoList,
  extractSkcSiteRules,
  extractSkuPriceInfoList,
  extractSkuSiteRules,
  pickOrdinaryMinRate,
  summarizeSheinEnrollResult,
  validateActivityPrice,
  validateDiscountRate,
} from '@/shein-platform/utils/sheinMarketingEnroll';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-product-ops.css';

function extractDetail(res) {
  if (!res || typeof res !== 'object') return null;
  if (res.info && typeof res.info === 'object' && (res.info.activityId != null || res.info.activityName)) {
    return res.info;
  }
  return res;
}

function extractProductsPayload(res) {
  if (!res || typeof res !== 'object') return { items: [], total: 0 };
  const nested = res.info && typeof res.info === 'object' ? res.info : res;
  const items = Array.isArray(nested.items)
    ? nested.items
    : (Array.isArray(nested.goodsInfoList) ? nested.goodsInfoList : []);
  const total = Number.isFinite(Number(nested.total))
    ? Number(nested.total)
    : (Number.isFinite(Number(nested.count)) ? Number(nested.count) : items.length);
  return { items, total, filterMissed: Boolean(nested.filterMissed) };
}

/**
 * 库存/规则列展示可填几折区间。
 * 平台 min/max*Rate 是降幅（% off）：最低降幅 1%、最高降幅 99% → 可填 1 ~ 99 折。
 */
function rateHint(siteRules) {
  const rule = Array.isArray(siteRules) ? siteRules[0] : null;
  if (!rule) return '';
  const minReduction = pickOrdinaryMinRate(rule);
  const maxReduction = rule.maxSupplyPriceRate ?? rule.maxSalePriceRate;
  const minZhe = maxReduction != null && Number.isFinite(Number(maxReduction))
    ? 100 - Number(maxReduction)
    : null;
  const maxZhe = minReduction != null ? 100 - Number(minReduction) : null;
  if (minZhe != null && maxZhe != null) return fixedUiTpl('{0} ~ {1} 折', minZhe, maxZhe);
  if (maxZhe != null) return fixedUiTpl('≤ {0} 折', maxZhe);
  if (minZhe != null) return fixedUiTpl('≥ {0} 折', minZhe);
  return '';
}

function skcOf(row) {
  return String(row?.skc || '').trim();
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** 报名任务轮询间隔（后端任务本身在 MQ Worker 上跑，轮询只为刷新进度） */
const ENROLL_JOB_POLL_MS = 700;
/** 轮询连续失败上限（含网络抖动），超过则按失败收尾 */
const ENROLL_JOB_MAX_POLL_ERRORS = 5;

/** SKU 列直显的最大行数，超出折叠成「…共 N 个 SKU」 */
const SKU_PREVIEW_LIMIT = 4;

/**
 * SKU 行尺码：平台字段优先，缺失时用后端按本地商品库（shein_product_spec_supply.size_label）补齐的 skuSizeLabel。
 * 取不到时返回空串，界面只显示 SKU 编码。
 */
function skuSizeOf(skuRow) {
  const candidates = [
    skuRow?.skuSizeLabel,
    skuRow?.skuSize,
    skuRow?.sizeLabel,
    skuRow?.size,
    skuRow?.sizeName,
  ];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const text = String(candidate).trim();
    if (text) return text;
  }
  return '';
}

/**
 * 几折 → 活动价（与提交侧一致：基准价 × (几折 / 100)）。
 * 例：99 折 → 0.99×价，1 折 → 0.01×价。缺失 / 非法时不展示。
 */
function computeDiscountedPrice(referencePrice, rate, currency) {
  const price = Number(referencePrice);
  const r = Number(rate);
  if (!Number.isFinite(price) || !Number.isFinite(r) || price <= 0) return '';
  if (String(rate ?? '').trim() === '') return '';
  return formatPrice((price * (r / 100)).toFixed(2), currency);
}

/**
 * 详情 / 可报商品请求单飞：同店同活动（同参数）的并发只发一次。
 * 多标签页同时打开、重复点击、路由重挂载时避免重复消耗 SHEIN 配额。
 */
const inflightDetail = new Map();
const inflightProducts = new Map();

function singleFlight(store, key, factory) {
  const existing = store.get(key);
  if (existing) return existing;
  const promise = (async () => {
    try {
      return await factory();
    } finally {
      store.delete(key);
    }
  })();
  store.set(key, promise);
  return promise;
}

/**
 * SHEIN 营销活动报名独立页（新标签打开，无双顶栏）。
 *
 * 版式与 TEMU 活动报名对齐：一行活动信息 → 一行查询 + 批量填写操作 → 可报商品表格。
 * 表格沿用「报名记录」设计：产品信息（SPU 维度 rowSpan）+ SKC（主图/颜色/SKC），
 * 勾选与填写都以 SKC 为最小单位。
 */
export default function SheinMarketingEnrollPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const { shopType, activityId: activityIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const slug = normalizeSheinShopTypeSlug(shopType) || 'semi';
  const isSelfOperated = slug === 'self';

  const shopId = String(searchParams.get('shopId') || '').trim();
  const activityNameQs = String(searchParams.get('name') || '').trim();
  const activityTypeQs = String(searchParams.get('activityType') || '').trim();
  const activityId = activityIdParam;

  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  /** 详情整体结束（含失败）：失败也放行商品表格，避免页面卡在空表 */
  const [detailSettled, setDetailSettled] = useState(false);
  const [detailWarning, setDetailWarning] = useState('');

  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);

  const [selectedSkcs, setSelectedSkcs] = useState(() => new Set());
  /** 跨页勾选缓存：key=SKC，value=可报商品行 */
  const [selectedProductMap, setSelectedProductMap] = useState(() => ({}));
  const [drafts, setDrafts] = useState({});
  const [selectedSites, setSelectedSites] = useState([]);
  /** 报名确认弹窗目标（null=关闭）：rows 为本次要报名的 SKC 行 */
  const [enrollConfirm, setEnrollConfirm] = useState(null);
  /**
   * 进行中的报名任务：jobId → 进度视图。
   * 允许多任务并行；仅锁定已进入任务的 SKC，其它 SKC 仍可继续提交。
   */
  const [enrollJobs, setEnrollJobs] = useState(() => ({}));
  /** 已进入异步报名任务、禁止重复提交的 SKC（state 驱动 UI，ref 防并发双点） */
  const [pendingEnrollSkcs, setPendingEnrollSkcs] = useState(() => new Set());
  const pendingEnrollSkcsRef = useRef(new Set());
  /** 页面存活：卸载后停止轮询更新（任务仍在后台跑） */
  const enrollAliveRef = useRef(true);

  /** 查询条件：输入框草稿 + 已生效的条件（field：skc / spu / sku） */
  const [queryDraft, setQueryDraft] = useState('');
  const [queryField, setQueryField] = useState('skc');
  const [appliedQuery, setAppliedQuery] = useState({ field: 'skc', tokens: [] });
  const [skcBatchOpen, setSkcBatchOpen] = useState(false);

  /** 批量填写：直接输入的数值（按字段 key 存），点「批量填写」时取第一个非空项 */
  const [batchInputs, setBatchInputs] = useState({});
  /** 批量填写「价格」时的口径（'' 表示跟随活动默认口径） */
  const [batchPriceMode, setBatchPriceMode] = useState('');
  /**
   * 价格填写口径（逐行可切）：key = SKC 或 `${skc}::${sku}`，value = 'price' | 'rate'。
   * 未设置时按活动默认口径（priceField）展示。
   */
  const [priceModes, setPriceModes] = useState({});

  const paramsReady = Boolean(shopId && activityId);

  useEffect(() => {
    document.title = fixedUi('活动报名 · SHEIN');
  }, []);

  /** 离开页面即停止轮询更新（任务仍在后台跑，可到报名记录查看结果） */
  useEffect(() => () => {
    enrollAliveRef.current = false;
  }, []);

  /**
   * 活动详情：后端本地优先（详情已入库，TTL 内直接读本地）+ 平台兜底，
   * 前端再做一次单飞，避免同时刻多个请求把平台 QPS 打满。
   * @param {{ refresh?: boolean }} [options] refresh=true 时跳过后端本地缓存（报名成功后刷新名额 / 是否可报）
   */
  const loadDetail = useCallback(async ({ refresh = false } = {}) => {
    if (!paramsReady) return;
    setDetailLoading(true);
    try {
      const res = await singleFlight(
        inflightDetail,
        `${shopId}:${activityId}:${refresh ? 'refresh' : 'cache'}`,
        () => sheinPlatformAPI.getMarketingActivityDetail(
          shopId,
          activityId,
          refresh ? { refresh: true } : {},
        ),
      );
      const next = extractDetail(res);
      setDetail(next);
      const sites = Array.isArray(next?.siteList) ? next.siteList.map(String) : [];
      setSelectedSites(sites);
      if (next?.detailIncomplete) {
        setDetailWarning(fixedUi('活动详情暂不可用，当前仅展示本地活动列表信息，请稍后重试或先同步活动列表。'));
      } else if (next?.detailStale) {
        setDetailWarning(fixedUi('平台详情暂时拉取失败，当前展示的是本地缓存的活动信息。'));
      } else {
        setDetailWarning('');
      }
    } catch (e) {
      if (!refresh) setDetail(null);
      setDetailWarning('');
      showToast(e?.message || t('marketing.enLoadDetailFail'), 'error');
    } finally {
      setDetailSettled(true);
      setDetailLoading(false);
    }
  }, [paramsReady, shopId, activityId, t]);

  const seedDrafts = useCallback((items, activityDetail, { reset = false } = {}) => {
    setDrafts((prev) => {
      const next = reset ? {} : { ...prev };
      items.forEach((row) => {
        const skc = skcOf(row);
        if (!skc) return;
        if (reset || next[skc] == null) {
          next[skc] = defaultDraftFromProduct(row, activityDetail);
        }
      });
      return next;
    });
  }, []);

  const loadProducts = useCallback(async (
    pageNo = page,
    size = pageSize,
    applied = appliedQuery,
  ) => {
    if (!paramsReady) return;
    setProductsLoading(true);
    setProductsError('');
    try {
      const body = {
        pageNum: Math.max(1, pageNo),
        pageSize: Math.max(1, size),
      };
      const tokens = Array.isArray(applied?.tokens) ? applied.tokens.filter(Boolean) : [];
      const field = applied?.field || 'skc';
      if (tokens.length) {
        if (field === 'spu') body.spuList = tokens;
        else if (field === 'sku') body.skuList = tokens;
        else body.skcList = tokens;
      }
      const res = await singleFlight(
        inflightProducts,
        `${shopId}:${activityId}:${body.pageNum}:${body.pageSize}:${field}:${tokens.join(',')}`,
        () => sheinPlatformAPI.listMarketingActivityProducts(shopId, activityId, body),
      );
      const payload = extractProductsPayload(res);
      if (payload.filterMissed) {
        showToast(fixedUi("本地商品库未匹配到该 SPU / SKU，请确认后重试"), 'warning');
      }
      setProducts(payload.items);
      setTotal(payload.total);
      seedDrafts(payload.items, detail, { reset: false });
      // 刷新已选 SKC 的缓存行，避免提交时用到过期规则
      setSelectedProductMap((prev) => {
        if (!prev || !Object.keys(prev).length) return prev;
        const next = { ...prev };
        payload.items.forEach((row) => {
          const skc = skcOf(row);
          if (skc && next[skc]) next[skc] = row;
        });
        return next;
      });
    } catch (e) {
      setProducts([]);
      setTotal(0);
      setProductsError(e?.message || fixedUi('加载可报商品失败'));
    } finally {
      setProductsLoading(false);
    }
  }, [paramsReady, shopId, activityId, page, pageSize, appliedQuery, seedDrafts, detail]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // 商品必须来自平台：详情成功或失败都放行（失败时用本地活动信息兜底，页面仍可用）
  useEffect(() => {
    if (!detailSettled) return;
    loadProducts(page, pageSize, appliedQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailSettled, detail, page, pageSize, appliedQuery]);

  const partakeFields = useMemo(() => {
    const list = Array.isArray(detail?.partakeFieldList) ? detail.partakeFieldList : [];
    return new Set(list.map((n) => Number(n)).filter((n) => Number.isFinite(n)));
  }, [detail]);

  const adjustForm = Number(detail?.adjustForm);
  /**
   * 填报项为空不能一概按「价格报名」处理：
   * 平台未配置调价形式（adjustForm=0）的活动不接受报价，报价格会被判「不支持该报价方式」。
   */
  const needPrice = partakeFields.has(1)
    || (partakeFields.size === 0 && adjustForm !== 0);
  const needActStock = partakeFields.has(2);
  const needReserve = partakeFields.has(3);
  const partakeScope = Number(detail?.partakeScope);
  const supportSelectSite = Number(detail?.supportSelectSitePartake) === 1;
  const siteOptions = useMemo(
    () => (Array.isArray(detail?.siteList) ? detail.siteList.map(String) : []),
    [detail],
  );

  /** 价格列实际写入的字段：自运营按「售价降幅」报价时只认 rate，其余认 price / rate */
  const priceField = useMemo(() => {
    if (!needPrice) return null;
    if (isSelfOperated && adjustForm === 6) return 'rate';
    return isSelfOperated ? 'price' : 'rate';
  }, [needPrice, isSelfOperated, adjustForm]);

  /**
   * 价格列既能填「调整后的价格」，也能填「折扣 %」，
   * 两种口径都可受理（自运营 adjustForm=6 除外，平台只认售价降幅）。
   */
  const priceColumnLabel = fixedUi('活动价格 / 折扣');

  /**
   * SKU 维度报价（partakeScope=1 且需报价格）：SKU / 供货价 / 活动价格 仍是三列，
   * 但三列之间不画竖线（看起来是一整块），改用行内虚线按 SKU 逐行划分。
   */
  const skuScopePrice = partakeScope === 1 && needPrice;
  /** 逐行可切口径：自运营 adjustForm=6 时只允许折扣 */
  const priceModeLocked = isSelfOperated && adjustForm === 6;
  /**
   * 参考价列：半托管看供货价，自运营看站点售价（与提交时算降幅的基准价一致）。
   * 返回 { value, currency }，缺失时 value 为 null。
   */
  const referencePriceOf = useCallback((priceRow) => ({
    value: isSelfOperated ? priceRow?.salePrice : priceRow?.supplyPrice,
    currency: priceRow?.currency,
  }), [isSelfOperated]);
  const referencePriceLabel = isSelfOperated ? fixedUi("售价") : fixedUi("供货价");

  /** 参考价列的一行（与 SKU / 价格列逐行等高对齐），dim=true 时带行间虚线 */
  const renderReferencePriceLine = (priceRow, key, dim = false) => {
    const base = referencePriceOf(priceRow);
    return (
      <div
        key={key}
        className={`spo-enroll-form-supply-line${dim ? ' spo-enroll-form-dim-line' : ''}`}
      >
        {base.value != null ? formatPrice(base.value, base.currency) : '—'}
      </div>
    );
  };

  const groups = useMemo(() => groupEnrollRowsBySpu(products), [products]);
  const canPickPage = products.length > 0;
  const allPageSelected = canPickPage
    && products.every((p) => selectedSkcs.has(skcOf(p)));

  /**
   * 列宽权重（列序：产品信息 | SKC | SKU | 供货价 | [活动件数] | [预留件数] | 活动价格/折扣 | 库存/规则 | 操作）。
   * 非复选框列合计按权重归一化到 100%，这样固定 50px 的复选框列不会被剩余空间再分配而变宽。
   */
  const enrollColWidths = useMemo(() => {
    const weights = [
      ['info', 19],
      ['skc', 12],
      ['sku', 11],
      ['supply', 9],
      ...(needActStock ? [['actStock', 7]] : []),
      ...(needReserve ? [['reserve', 7]] : []),
      ...(needPrice ? [['price', 18]] : []),
      ['rule', 9],
      ['ops', 7],
    ];
    const total = weights.reduce((sum, [, weight]) => sum + weight, 0);
    return weights.reduce((acc, [key, weight]) => {
      acc[key] = `${((weight / total) * 100).toFixed(4)}%`;
      return acc;
    }, {});
  }, [needActStock, needReserve, needPrice]);

  /**
   * 批量填写字段：按表格列序取最多两个可填项，直接输入数值后点「批量填写」生效。
   */
  const batchFields = useMemo(() => {
    const list = [];
    if (needActStock) {
      list.push({ key: 'actStock', kind: 'actStock', get label() { return fixedUi("活动件数"); }, get placeholder() { return fixedUi("活动件数"); } });
    }
    if (needReserve) {
      list.push({ key: 'reserveStock', kind: 'reserveStock', get label() { return fixedUi("预留件数"); }, get placeholder() { return fixedUi("预留件数"); } });
    }
    if (needPrice) {
      const switchable = !priceModeLocked;
      list.push({
        key: 'price',
        kind: 'price',
        switchable,
        mode: switchable
          ? (batchPriceMode || (priceField === 'rate' ? 'rate' : 'price'))
          : 'rate',
        label: priceColumnLabel,
        placeholder: priceColumnLabel,
      });
    }
    return list.slice(0, 2);
  }, [
    needActStock,
    needReserve,
    needPrice,
    priceField,
    priceColumnLabel,
    batchPriceMode,
    priceModeLocked,
  ]);

  const updateDraft = (skc, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [skc]: { ...(prev[skc] || {}), ...patch },
    }));
  };

  const updateSkuDraft = (skc, sku, patch) => {
    setDrafts((prev) => {
      const cur = prev[skc] || {};
      const skus = { ...(cur.skus || {}) };
      skus[sku] = { ...(skus[sku] || {}), ...patch };
      return { ...prev, [skc]: { ...cur, skus } };
    });
  };

  /** 切换某行价格填写口径；切换时不做清空，输入时由调用方保证另一侧为空 */
  const setPriceMode = (modeKey, mode) => {
    setPriceModes((prev) => ({ ...prev, [modeKey]: mode }));
  };

  /**
   * 写价格：填「调整价」时清掉折扣，反之亦然，避免两种口径同时存在。
   * - 调整价必须严格小于基准价（供货价 / 售价）
   * - 折扣按几折填写，比例不能大于 1（填写值须 < 100）
   * 未完成输入（空串 / 仅小数点）放行；输入过程不强制「>0」，完整数字越界时 toast 并拒绝写入。
   */
  const writePrice = (skc, sku, field, value, referencePrice) => {
    const raw = value == null ? '' : String(value);
    const trimmed = raw.trim();
    if (trimmed !== '' && trimmed !== '-' && trimmed !== '.' && trimmed !== '-.') {
      const n = Number(trimmed);
      if (Number.isFinite(n)) {
        const err = field === 'rate'
          ? validateDiscountRate(n, { requirePositive: false })
          : validateActivityPrice(n, referencePrice, referencePriceLabel, { requirePositive: false });
        if (err) {
          showToast(err, 'warning');
          return false;
        }
      }
    }
    const patch = field === 'rate'
      ? { rate: value, price: '' }
      : { price: value, rate: '' };
    if (sku) updateSkuDraft(skc, sku, patch);
    else updateDraft(skc, patch);
    return true;
  };

  const toggleSkc = (skc, row) => {
    setSelectedSkcs((prev) => {
      const next = new Set(prev);
      if (next.has(skc)) next.delete(skc);
      else next.add(skc);
      return next;
    });
    setSelectedProductMap((prev) => {
      const next = { ...prev };
      if (next[skc]) {
        delete next[skc];
      } else if (row) {
        next[skc] = row;
      }
      return next;
    });
  };

  const toggleAllPage = () => {
    const keys = products.map(skcOf).filter(Boolean);
    setSelectedSkcs((prev) => {
      const next = new Set(prev);
      if (allPageSelected) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
    setSelectedProductMap((prev) => {
      const next = { ...prev };
      if (allPageSelected) {
        keys.forEach((k) => { delete next[k]; });
      } else {
        products.forEach((p) => {
          const k = skcOf(p);
          if (k) next[k] = p;
        });
      }
      return next;
    });
  };

  const toggleSite = (site) => {
    setSelectedSites((prev) => {
      if (prev.includes(site)) return prev.filter((s) => s !== site);
      return [...prev, site];
    });
  };

  const backToList = () => {
    navigate(buildSheinPath(slug, 'marketing'));
  };

  const onQuery = () => {
    const tokens = parseBatchTokens(queryDraft).slice(0, BATCH_QUERY_LIMIT);
    const applied = { field: queryField, tokens };
    setAppliedQuery(applied);
    if (page === 1) loadProducts(1, pageSize, applied);
    else setPage(1);
  };

  const onResetQuery = () => {
    setQueryDraft('');
    const applied = { field: queryField, tokens: [] };
    setAppliedQuery(applied);
    if (page === 1) loadProducts(1, pageSize, applied);
    else setPage(1);
  };

  const batchTargets = useMemo(() => {
    if (selectedSkcs.size > 0) {
      return Array.from(selectedSkcs)
        .map((skc) => selectedProductMap[skc] || products.find((p) => skcOf(p) === skc))
        .filter(Boolean);
    }
    return products;
  }, [selectedSkcs, selectedProductMap, products]);

  const handleBatchFillConfirm = ({ kind, mode, value }) => {
    const targets = batchTargets;
    if (!targets.length) {
      showToast(fixedUi("当前没有可填写的商品"), 'warning');
      return;
    }
    const key = kind === 'price'
      ? (mode === 'rate' ? 'rate' : 'price')
      : kind;
    if (kind === 'price') {
      if (mode === 'rate') {
        const err = validateDiscountRate(value);
        if (err) {
          showToast(err, 'warning');
          return;
        }
      } else {
        const n = Number(value);
        if (Number.isFinite(n)) {
          const bad = targets.some((row) => {
            if (partakeScope === 1) {
              return (Array.isArray(row.skuInfoList) ? row.skuInfoList : []).some((skuRow) => {
                const base = referencePriceOf(extractSkuPriceInfoList(skuRow)[0]).value;
                return Boolean(validateActivityPrice(n, base, referencePriceLabel));
              });
            }
            const base = referencePriceOf(extractSkcPriceInfoList(row)[0]).value;
            return Boolean(validateActivityPrice(n, base, referencePriceLabel));
          });
          if (bad) {
            showToast(fixedUiTpl('活动价必须小于{0}', referencePriceLabel), 'warning');
            return;
          }
        }
      }
    }
    /** 价格列是逐行口径：批量填写时同步把每行切到本次口径，避免旧口径覆盖 */
    const nextModes = {};
    setDrafts((prev) => {
      const next = { ...prev };
      targets.forEach((row) => {
        const skc = skcOf(row);
        if (!skc) return;
        const cur = next[skc] || {};
        if (kind === 'price') {
          const pricePatch = mode === 'rate' ? { rate: value, price: '' } : { price: value, rate: '' };
          if (partakeScope === 1) {
            const skus = { ...(cur.skus || {}) };
            (Array.isArray(row.skuInfoList) ? row.skuInfoList : []).forEach((skuRow) => {
              const code = String(skuRow?.sku || '').trim();
              if (!code) return;
              skus[code] = { ...(skus[code] || {}), ...pricePatch };
              nextModes[`${skc}::${code}`] = key;
            });
            next[skc] = { ...cur, ...pricePatch, skus };
            return;
          }
          nextModes[skc] = key;
          next[skc] = { ...cur, ...pricePatch };
          return;
        }
        next[skc] = { ...cur, [key]: value };
      });
      return next;
    });
    if (Object.keys(nextModes).length) {
      setPriceModes((prev) => ({ ...prev, ...nextModes }));
    }
    showToast(fixedUiTpl('已批量填写（{0} 个 SKC）', targets.length), 'success');
  };

  /**
   * 批量填写（行内直接输入）：多个输入框都填了则只取第一个，
   * 填报范围沿用「有勾选填已勾选，未勾选填当前页」。
   */
  const handleInlineBatchFill = () => {
    const picked = batchFields.find((field) => (
      String(batchInputs[field.key] ?? '').trim() !== ''
    ));
    if (!picked) {
      showToast(fixedUiTpl('请先输入{0}', batchFields.map((f) => f.label).join(fixedUi("或")) || fixedUi("要填写的数据")), 'warning');
      return;
    }
    handleBatchFillConfirm({
      kind: picked.kind,
      mode: picked.mode,
      value: String(batchInputs[picked.key]).trim(),
    });
  };

  /** 报名前置校验 + 弹确认框：真正提交在用户点「确认报名」后走后台异步任务 */
  const requestEnroll = (targetRows) => {
    if (!paramsReady || !detail) {
      showToast(fixedUi("活动信息未就绪"), 'warning');
      return;
    }
    if (Number(detail.canPartake) === 0) {
      showToast(detail.cannotPartakeReason || fixedUi('当前活动不可报名'), 'warning');
      return;
    }
    if (detail.detailIncomplete) {
      showToast(fixedUi("活动详情未就绪（当前为本地活动列表兜底），请稍后重试后再提交"), 'warning');
      return;
    }
    const selectedProducts = (Array.isArray(targetRows) && targetRows.length
      ? targetRows
      : Array.from(selectedSkcs)
        .map((skc) => selectedProductMap[skc] || products.find((p) => skcOf(p) === skc))
        .filter(Boolean)
    ).filter(Boolean);
    if (!selectedProducts.length) {
      showToast(fixedUi("请先勾选要报名的商品"), 'warning');
      return;
    }
    // 已在异步任务中的 SKC 不可再报；其余可继续提交新任务
    const busySkcs = selectedProducts.map(skcOf).filter((k) => k && pendingEnrollSkcs.has(k));
    const freeProducts = selectedProducts.filter((row) => {
      const k = skcOf(row);
      return k && !pendingEnrollSkcs.has(k);
    });
    if (!freeProducts.length) {
      showToast(fixedUi("所选商品均已在报名任务中，请等待完成后再试"), 'warning');
      return;
    }
    if (busySkcs.length) {
      showToast(fixedUiTpl('已跳过 {0} 个进行中的 SKC，将提交其余 {1} 个', busySkcs.length, freeProducts.length), 'warning');
    }
    if (freeProducts.length > 100) {
      showToast(fixedUi("单次最多报名 100 个 SKC，请分批提交"), 'warning');
      return;
    }
    // 弹确认框前先组装校验（活动价 < 供货价、折扣比例 ≤ 1），避免点确认才报错
    try {
      buildSheinCampaignGoodsInfoList({
        detail,
        products: freeProducts,
        drafts,
        selectedSites,
        isSelfOperated,
      });
    } catch (e) {
      showToast(e?.message || fixedUi('报名数据校验未通过'), 'warning');
      return;
    }
    setEnrollConfirm({ rows: freeProducts, single: freeProducts.length === 1 });
  };

  /** 轮询单个报名任务；页面卸载后返回 null（不影响其它并行任务） */
  const pollEnrollJob = async (jobId) => {
    let errors = 0;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(ENROLL_JOB_POLL_MS);
      if (!enrollAliveRef.current) return null;
      let view;
      try {
        // eslint-disable-next-line no-await-in-loop
        view = await sheinPlatformAPI.getMarketingEnrollJob(shopId, jobId);
        errors = 0;
      } catch (e) {
        errors += 1;
        if (errors >= ENROLL_JOB_MAX_POLL_ERRORS) throw e;
        // eslint-disable-next-line no-continue
        continue;
      }
      if (!enrollAliveRef.current) return null;
      setEnrollJobs((prev) => ({
        ...prev,
        [jobId]: { ...(view || {}), jobId, visible: true },
      }));
      if (view?.done) return view;
    }
  };

  const releasePendingSkcs = (keys) => {
    if (!keys?.length) return;
    keys.forEach((k) => pendingEnrollSkcsRef.current.delete(k));
    setPendingEnrollSkcs(new Set(pendingEnrollSkcsRef.current));
  };

  const claimPendingSkcs = (keys) => {
    if (!keys?.length) return false;
    const overlap = keys.filter((k) => pendingEnrollSkcsRef.current.has(k));
    if (overlap.length) {
      showToast(
        fixedUiTpl('以下 SKC 已在报名任务中，请勿重复提交：{0}{1}', overlap.slice(0, 5).join('、'), overlap.length > 5 ? '…' : ''),
        'warning',
      );
      return false;
    }
    keys.forEach((k) => pendingEnrollSkcsRef.current.add(k));
    setPendingEnrollSkcs(new Set(pendingEnrollSkcsRef.current));
    return true;
  };

  /**
   * 提交后台报名任务：可与其它任务并行；仅锁定本次 SKC。
   * HTTP 只拿到 jobId + queued，实际由 MQ Worker（无 MQ 时本机池）执行。
   */
  const startEnrollJob = async (rows) => {
    const selectedProducts = Array.isArray(rows) ? rows.filter(Boolean) : [];
    if (!selectedProducts.length) return;
    const keys = selectedProducts.map(skcOf).filter(Boolean);
    if (!keys.length) return;
    if (!claimPendingSkcs(keys)) return;

    let jobId = '';
    try {
      const goodsInfoList = buildSheinCampaignGoodsInfoList({
        detail,
        products: selectedProducts,
        drafts,
        selectedSites,
        isSelfOperated,
      });
      const started = await sheinPlatformAPI.submitMarketingEnroll(shopId, activityId, { goodsInfoList });
      jobId = String(started?.jobId || '').trim();
      if (!jobId) throw new Error(fixedUi('未返回报名任务 ID，请稍后重试'));

      setEnrollJobs((prev) => ({
        ...prev,
        [jobId]: {
          ...(started || {}),
          jobId,
          visible: true,
          status: started?.status || 'queued',
          percent: Number(started?.percent) || 2,
          message: started?.message || '排队中…',
          skcKeys: keys,
        },
      }));
      showToast(fixedUiTpl('已提交后台报名（{0} 个 SKC），可继续报名其它商品', goodsInfoList.length), 'success');

      const finalView = started?.done ? started : await pollEnrollJob(jobId);
      if (!finalView || !enrollAliveRef.current) return;

      const payload = finalView?.result && typeof finalView.result === 'object'
        ? finalView.result
        : finalView;
      const summary = summarizeSheinEnrollResult(payload);
      const tone = summary.failCount > 0 && summary.successCount === 0
        ? 'error'
        : (summary.failCount > 0 ? 'warning' : 'success');
      showToast(summary.message, tone);

      // 进度条保留片刻再收起
      const doneJobId = jobId;
      setTimeout(() => {
        if (!enrollAliveRef.current) return;
        setEnrollJobs((prev) => {
          if (!prev[doneJobId]) return prev;
          const next = { ...prev };
          delete next[doneJobId];
          return next;
        });
      }, 3000);

      if (summary.successCount > 0) {
        const doneKeys = new Set(keys);
        setSelectedSkcs((prev) => {
          const next = new Set(prev);
          doneKeys.forEach((k) => next.delete(k));
          return next;
        });
        setSelectedProductMap((prev) => {
          const next = { ...prev };
          doneKeys.forEach((k) => { delete next[k]; });
          return next;
        });
        await loadProducts(page, pageSize);
        await loadDetail({ refresh: true });
      }
    } catch (e) {
      showToast(e?.message || fixedUi('报名失败'), 'error');
      if (jobId) {
        setEnrollJobs((prev) => {
          if (!prev[jobId]) return prev;
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
      }
    } finally {
      releasePendingSkcs(keys);
    }
  };

  const confirmEnroll = () => {
    const target = enrollConfirm;
    setEnrollConfirm(null);
    if (!target?.rows?.length) return;
    // 不 await：允许多个报名任务并行排队
    startEnrollJob(target.rows);
  };

  const activeEnrollJobs = useMemo(
    () => Object.values(enrollJobs).filter(Boolean),
    [enrollJobs],
  );

  if (!paramsReady) {
    return (
      <div className="spo-marketing-enroll">
        <p className="spo-empty-hint">{t('marketing.enSheinMissing')}</p>
        <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={backToList}>
          {t('marketing.enBack')}
        </button>
      </div>
    );
  }

  const titleName = detail?.activityName || activityNameQs || fixedUiTpl('活动 {0}', activityId);
  const typeLabel = activityTypeLabel(detail?.activityType ?? activityTypeQs);
  const canPartake = detail == null ? null : Number(detail.canPartake) === 1;
  /** 详情字段不全（本地活动列表兜底）：不允许提交，避免把不完整字段发到平台 */
  const detailIncomplete = Boolean(detail?.detailIncomplete);
  const fieldLabels = partakeFieldLabels(detail?.partakeFieldList);
  const colSpan = 7
    + (needActStock ? 1 : 0)
    + (needReserve ? 1 : 0)
    + (needPrice ? 1 : 0);

  /**
   * 价格填写单元：既可填「调整后的价格」，也可切换成「几折」。
   * - 切到折扣时，右侧自动算出活动价（基准价 × 几折/100；99→0.99×价，1→0.01×价）
   * - 直接填调整价时不显示计算结果
   * - 未勾选 SKC 也可以直接填写（勾选只用于批量填写范围）
   * - 自运营且 adjustForm=6（平台只认售价降幅）时锁定为折扣，提交时几折→降幅
   * - fill=true（SKU 表单行内）：价格表单占满该行剩余宽度
   */
  const renderPriceInput = ({
    modeKey,
    priceValue,
    rateValue,
    supplyPrice,
    supplyCurrency,
    label,
    fill = false,
    onChangePrice,
    onChangeRate,
  }) => {
    const allowSwitch = !priceModeLocked;
    const mode = allowSwitch
      ? (priceModes[modeKey] || (priceField === 'rate' ? 'rate' : 'price'))
      : 'rate';
    const isRate = mode === 'rate';
    const computed = isRate
      ? computeDiscountedPrice(supplyPrice, rateValue, supplyCurrency)
      : '';
    const inputMax = isRate
      ? 99.99
      : (Number.isFinite(Number(supplyPrice)) ? Number(supplyPrice) : undefined);
    const onInputChange = (raw) => (isRate ? onChangeRate(raw) : onChangePrice(raw));
    return (
      <div className={`spo-enroll-form-price-cell${fill ? ' spo-enroll-form-price-cell--fill' : ''}`}>
        {fill ? (
          <div className="spo-enroll-form-price-input-slot">
            <input
              className="spo-input"
              type="number"
              min={0}
              max={inputMax}
              step="any"
              value={(isRate ? rateValue : priceValue) ?? ''}
              placeholder={isRate ? fixedUi("折扣") : fixedUi("调整价")}
              aria-label={`${label} ${isRate ? fixedUi('折扣百分比') : fixedUi('调整后价格')}`}
              onChange={(e) => onInputChange(e.target.value)}
            />
          </div>
        ) : (
          <input
            className="spo-input"
            type="number"
            min={0}
            max={inputMax}
            step="any"
            value={(isRate ? rateValue : priceValue) ?? ''}
            placeholder={isRate ? fixedUi("折扣") : fixedUi("调整价")}
            aria-label={`${label} ${isRate ? fixedUi('折扣百分比') : fixedUi('调整后价格')}`}
            onChange={(e) => onInputChange(e.target.value)}
          />
        )}
        {allowSwitch ? (
          <select
            className="spo-select spo-enroll-form-price-unit"
            value={mode}
            aria-label={fixedUiTpl('{0} 填写口径', label)}
            title={fixedUi("切换填写口径：调整价 / 折扣")}
            onChange={(e) => setPriceMode(modeKey, e.target.value)}
          >
            <option value="price">{fixedUi("调整价")}</option>
            <option value="rate">{fixedUi("折扣")}</option>
          </select>
        ) : (
          <span className="spo-enroll-form-price-unit-static" aria-hidden="true">{fixedUi("折扣")}</span>
        )}
        {isRate && computed ? (
          <span className="spo-enroll-form-price-computed" title={fixedUi("按折扣自动计算的活动价")}>
            {computed}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <div className="spo-marketing-enroll spo-enroll-form">
      {/* 第一行：活动信息（描述 + 日期同一行） + 右上角 返回 / 提交报名 */}
      <section className="spo-card spo-enroll-head">
        <div className="spo-enroll-head__main">
          <h1 className="spo-enroll-head__title" title={titleName}>{titleName}</h1>
          <div className="spo-enroll-head__meta">
            <span className="spo-enroll-chip">{typeLabel}</span>
            {canPartake != null ? (
              <span className={`spo-enroll-chip ${canPartake ? 'spo-enroll-chip--ok' : 'spo-enroll-chip--warn'}`}>
                {canPartake ? fixedUi('可报名') : fixedUi('暂不可报名')}
              </span>
            ) : null}
            <span className="spo-enroll-chip spo-enroll-chip--muted">{fixedUi('活动 ID')} {activityId}</span>
            {detail ? (
              <>
                <span className="spo-enroll-chip spo-enroll-chip--muted">
                  {partakeScopeLabel(detail.partakeScope)}
                </span>
                <span className="spo-enroll-chip spo-enroll-chip--muted">
                  {adjustFormLabel(detail.adjustForm)}
                </span>
                <span className="spo-enroll-chip spo-enroll-chip--muted">
                  {fixedUi('填报项')} {fieldLabels.join('、') || fixedUi('无需填报')}
                </span>
                <span className="spo-enroll-head__times">
                  <span>{fixedUi('报名时间：')}{formatTimeRange(detail.partakeStartTime, detail.partakeEndTime)}</span>
                  <span>{fixedUi('活动时间：')}{formatTimeRange(detail.activityStartTime, detail.activityEndTime)}</span>
                </span>
              </>
            ) : (
              <span className="spo-enroll-chip spo-enroll-chip--muted">
                {detailLoading ? fixedUi('加载活动详情中…') : fixedUi('活动详情未就绪')}
              </span>
            )}
          </div>
          {detail && !canPartake ? (
            <p className="spo-enroll-head__warn">
              {detail.cannotPartakeReason || fixedUi('当前店铺不可报名该活动')}
            </p>
          ) : null}
          {detailWarning ? (
            <p className="spo-enroll-head__warn">{detailWarning}</p>
          ) : null}
        </div>
        <div className="spo-enroll-head__side">
          <button
            type="button"
            className="spo-btn spo-btn--secondary"
            onClick={backToList}
          >
            {t('marketing.enBack')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary"
            disabled={productsLoading || detailIncomplete || Number(detail?.canPartake) === 0}
            onClick={() => requestEnroll()}
          >
            {activeEnrollJobs.length > 0
              ? `${fixedUi('提交报名')}（${fixedUi('已选')} ${selectedSkcs.size}，${fixedUi('进行中')} ${activeEnrollJobs.length}）`
              : `${fixedUi('提交报名')}（${fixedUi('已选')} ${selectedSkcs.size}）`}
          </button>
        </div>
      </section>

      {activeEnrollJobs.length > 0 ? (
        <div className="spo-enroll-job">
          {activeEnrollJobs.map((job) => (
            <SheinSyncProgressBar
              key={job.jobId}
              progress={{
                visible: true,
                status: job.status || 'running',
                percent: job.percent,
                message: job.message,
                label: fixedUi('报名'),
              }}
            />
          ))}
          <p className="spo-enroll-job__hint">
            {activeEnrollJobs.length > 1
              ? `${fixedUi('当前有')} ${activeEnrollJobs.length} ${fixedUi('个报名任务在后台执行')}`
              : fixedUi('报名任务已在后台异步执行；已锁定本任务 SKC，其它商品可继续提交。完成后可到「报名记录」查看审核状态。')}
          </p>
        </div>
      ) : null}

      {/* 第二行：左侧批量填写（直接输入） + 右侧查询 */}
      <ProductFilterCard>
        <div className="spo-enroll-form__toolbar">
          {batchFields.length > 0 ? (
            <div className="spo-enroll-form__batch">
              {batchFields.map((field) => (
                <span key={field.key} className="spo-enroll-form__batch-item">
                  <input
                    className="spo-input spo-input--sm spo-enroll-form__batch-input"
                    type="number"
                    min={0}
                    step="any"
                    value={batchInputs[field.key] ?? ''}
                    placeholder={field.placeholder}
                    aria-label={fixedUiTpl('批量填写{0}', field.label)}
                    onChange={(e) => {
                      const next = e.target.value;
                      setBatchInputs((prev) => ({ ...prev, [field.key]: next }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleInlineBatchFill();
                    }}
                  />
                  {field.switchable ? (
                    <select
                      className="spo-select spo-enroll-form__batch-unit"
                      value={field.mode}
                      aria-label={fixedUi("批量填写口径")}
                      title={fixedUi("切换批量填写口径：调整价 / 折扣")}
                      onChange={(e) => setBatchPriceMode(e.target.value)}
                    >
                      <option value="price">{fixedUi("调整价")}</option>
                      <option value="rate">{fixedUi("折扣")}</option>
                    </select>
                  ) : null}
                </span>
              ))}
              <button
                type="button"
                className="spo-btn spo-btn--secondary spo-btn--sm"
                disabled={productsLoading || !products.length}
                onClick={handleInlineBatchFill}
              >
                {t('marketing.enBatchFill')}
              </button>
            </div>
          ) : null}
          <div className="spo-enroll-form__query">
            <select
              className="spo-select spo-enroll-form__query-field"
              value={queryField}
              aria-label={fixedUi("查询字段")}
              onChange={(e) => setQueryField(e.target.value)}
            >
              <option value="skc">SKC</option>
              <option value="spu">SPU</option>
              <option value="sku">SKU</option>
            </select>
            <div className="spo-enroll-form__query-input">
              <input
                className="spo-input"
                type="search"
                value={queryDraft}
                placeholder={fixedUiTpl('{0}，支持逗号批量', queryField.toUpperCase())}
                aria-label={fixedUiTpl('{0} 查询', queryField.toUpperCase())}
                onChange={(e) => setQueryDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onQuery();
                }}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={fixedUi("批量输入")}
                title={fixedUi("批量查询")}
                onClick={() => setSkcBatchOpen(true)}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              disabled={productsLoading}
              onClick={onResetQuery}
            >
              {t('shop.reset')}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={productsLoading}
              onClick={onQuery}
            >
              {t('shop.query')}
            </button>
          </div>
        </div>

        {supportSelectSite && siteOptions.length > 0 ? (
          <div className="spo-enroll-form__sites">
            <span className="spo-enroll-form__sites-label">{fixedUi("报名站点")}</span>
            <div className="spo-marketing-enroll__site-list">
              {siteOptions.map((site) => (
                <label key={site} className="spo-marketing-enroll__site-item">
                  <input
                    type="checkbox"
                    checked={selectedSites.includes(site)}
                    onChange={() => toggleSite(site)}
                  />
                  <span>{site}</span>
                </label>
              ))}
            </div>
          </div>
        ) : null}

        {productsError ? (
          <div className="spo-ops-error" role="alert">{productsError}</div>
        ) : null}
      </ProductFilterCard>

      {/* 第三行：可报商品（SPU 维度分组，SKC 粒度勾选 / 填写） */}
      <ProductListCard loading={productsLoading} loadingLabel={fixedUi("正在拉取平台可报商品…")}>
        <div className="spo-drafts__table-wrap spo-ops-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-ops-table spo-ops-table--sticky-head spo-marketing-enrolls-table spo-enroll-form-table">
            <colgroup>
              <col className="spo-ops-col spo-ops-col--check shein-product-col--check" />
              <col className="spo-enroll-form-col--info" style={{ width: enrollColWidths.info }} />
              <col className="spo-enroll-form-col--skc" style={{ width: enrollColWidths.skc }} />
              <col className="spo-enroll-form-col--sku" style={{ width: enrollColWidths.sku }} />
              <col className="spo-enroll-form-col--supply" style={{ width: enrollColWidths.supply }} />
              {needActStock ? (
                <col className="spo-enroll-form-col--field" style={{ width: enrollColWidths.actStock }} />
              ) : null}
              {needReserve ? (
                <col className="spo-enroll-form-col--field" style={{ width: enrollColWidths.reserve }} />
              ) : null}
              {needPrice ? (
                <col className="spo-enroll-form-col--price" style={{ width: enrollColWidths.price }} />
              ) : null}
              <col className="spo-enroll-form-col--rule" style={{ width: enrollColWidths.rule }} />
              <col className="spo-enroll-form-col--ops" style={{ width: enrollColWidths.ops }} />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-ops-th spo-ops-th--check shein-product-cell--spu-check"
                  onClick={() => {
                    if (!canPickPage || productsLoading) return;
                    toggleAllPage();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={fixedUi("全选当前页 SKC")}
                    disabled={!canPickPage || productsLoading}
                    checked={allPageSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAllPage}
                  />
                </th>
                <th scope="col">{fixedUi("产品信息")}</th>
                <th scope="col">SKC</th>
                <th scope="col" className="spo-enroll-form-th--center">SKU</th>
                <th scope="col" className="spo-enroll-form-th--center">{referencePriceLabel}</th>
                {needActStock ? (
                  <th scope="col" className="spo-enroll-form-th--center">{fixedUi("活动件数")}</th>
                ) : null}
                {needReserve ? (
                  <th scope="col" className="spo-enroll-form-th--center">{fixedUi("预留件数")}</th>
                ) : null}
                {needPrice ? (
                  <th scope="col" className="spo-enroll-form-th--start">{priceColumnLabel}</th>
                ) : null}
                <th scope="col" className="spo-enroll-form-th--center">{fixedUi("库存 / 规则")}</th>
                <th scope="col" className="spo-enroll-form-th--center">{fixedUi("操作")}</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && !productsLoading ? (
                <tr>
                  <td colSpan={colSpan} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">{fixedUi("暂无可报商品")}</p>
                      <p className="spo-certs-empty__desc">
                        {t('marketing.enSheinEmptyHint')}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : null}
              {productsLoading ? (
                <tr className="spo-enroll-form-loading-row">
                  <td colSpan={colSpan}>
                    <div className="spo-enroll-form-loading" role="status" aria-live="polite">
                      <i className="spo-enroll-form-loading__spinner" aria-hidden="true" />
                      <span>{fixedUi("正在拉取平台可报商品…")}</span>
                    </div>
                  </td>
                </tr>
              ) : null}
              {productsLoading ? null : groups.flatMap((group) => {
                const groupSize = group.items.length;
                const rows = [];
                group.items.forEach(({ row }, rowIdx) => {
                  const isFirst = rowIdx === 0;
                  const skc = skcOf(row);
                  const info = enrollProductInfo(row);
                  const draft = drafts[skc] || {};
                  const skcRules = extractSkcSiteRules(row);
                  const skcPrices = extractSkcPriceInfoList(row);
                  const skuList = Array.isArray(row.skuInfoList) ? row.skuInfoList : [];
                  const selected = selectedSkcs.has(skc);

                  rows.push(
                    <tr
                      key={skc || `${group.key}-${rowIdx}`}
                      className={[
                        'spo-marketing-enroll-row',
                        'spo-enroll-form-row--selectable',
                        isFirst ? '' : 'spo-marketing-enroll-row--child',
                        selected ? 'is-selected' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => toggleSkc(skc, row)}
                    >
                      <td className="spo-ops-td spo-ops-td--check shein-product-cell shein-product-cell--spu-check">
                        <input
                          type="checkbox"
                          aria-label={fixedUiTpl('选择 {0}', skc)}
                          checked={selected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSkc(skc, row)}
                        />
                      </td>
                      {isFirst ? (
                        <td
                          rowSpan={groupSize}
                          className="shein-product-cell shein-product-cell--info spo-marketing-enroll-group"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="shein-product-info shein-product-info--text-only spo-marketing-enroll-product spo-enroll-form-product">
                            <HoverCopyField
                              value={group.product.title || fixedUi('未命名商品')}
                              className="shein-product-info__title"
                              popoverCopy
                            />
                            <HoverCopyField
                              label="SPU"
                              value={group.product.spu}
                              className="shein-product-info__meta"
                              separator=": "
                            />
                            <HoverCopyField
                              label={fixedUi("货号")}
                              value={group.product.supplierCode}
                              className="shein-product-info__meta"
                              separator=": "
                            />
                            <span className="shein-product-info__meta">
                              {fixedUi('SKC 数')}: {groupSize}
                            </span>
                          </div>
                        </td>
                      ) : null}
                      <td className="shein-product-cell shein-product-cell--spec">
                        <div className="shein-product-spec spo-marketing-enroll-skc">
                          <SheinProductThumb imageUrl={info.imageUrl} alt={info.skc || 'SKC'} />
                          <div className="shein-product-spec__text">
                            <div className="shein-product-spec__color">
                              {info.color || '—'}
                            </div>
                            <HoverCopyField
                              label="SKC"
                              value={info.skc}
                              className="shein-product-spec__skc"
                              separator=": "
                            />
                          </div>
                        </div>
                      </td>
                      <td className={`spo-enroll-form-td--sku${skuScopePrice ? ' spo-enroll-form-td--dim-start' : ''}`}>
                        <div className={`spo-enroll-form-sku-list${skuScopePrice ? ' spo-enroll-form-dim-list' : ''}`}>
                          {skuList.length === 0 ? (
                            <span className="spo-muted">—</span>
                          ) : (
                            <>
                              {(skuScopePrice ? skuList : skuList.slice(0, SKU_PREVIEW_LIMIT)).map((skuRow, skuIdx) => {
                                const code = String(skuRow?.sku || '').trim();
                                const size = skuSizeOf(skuRow);
                                return (
                                  <div
                                    key={`${code || 'sku'}-${skuIdx}`}
                                    className={skuScopePrice
                                      ? 'spo-enroll-form-sku-line spo-enroll-form-dim-line'
                                      : 'shein-product-sku-list__line'}
                                  >
                                    <span className="spo-enroll-form-sku-cell">
                                      {size ? (
                                        <span className="spo-enroll-form-sku-size">{size}：</span>
                                      ) : null}
                                      <HoverCopyField value={code || `SKU ${skuIdx + 1}`} />
                                    </span>
                                  </div>
                                );
                              })}
                              {!skuScopePrice && skuList.length > SKU_PREVIEW_LIMIT ? (
                                <div className="spo-muted">
                                  {fixedUiTpl('…共 {0} 个 SKU', skuList.length)}
                                </div>
                              ) : null}
                            </>
                          )}
                        </div>
                      </td>
                      <td className={`spo-enroll-form-td--supply${skuScopePrice ? ' spo-enroll-form-td--dim-mid' : ''}`}>
                        <div className={`spo-enroll-form-supply-list${skuScopePrice ? ' spo-enroll-form-dim-list' : ''}`}>
                          {skuScopePrice
                            ? skuList.map((skuRow, skuIdx) => renderReferencePriceLine(
                              extractSkuPriceInfoList(skuRow)[0],
                              `sku-base-${skuIdx}`,
                              true,
                            ))
                            : renderReferencePriceLine(skcPrices[0], 'skc-base')}
                        </div>
                      </td>
                      {needActStock ? (
                        <td
                          className="spo-enroll-form-td--center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            className="spo-input spo-input--sm"
                            value={draft.actStock || ''}
                            placeholder={fixedUi("件数")}
                            aria-label={fixedUiTpl('{0} 活动件数', skc)}
                            onChange={(e) => updateDraft(skc, { actStock: e.target.value })}
                          />
                        </td>
                      ) : null}
                      {needReserve ? (
                        <td
                          className="spo-enroll-form-td--center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            className="spo-input spo-input--sm"
                            value={draft.reserveStock || ''}
                            placeholder={fixedUi("预留")}
                            aria-label={fixedUiTpl('{0} 活动预留件数', skc)}
                            onChange={(e) => updateDraft(skc, { reserveStock: e.target.value })}
                          />
                        </td>
                      ) : null}
                      {needPrice ? (
                        skuScopePrice ? (
                          <td
                            className="spo-enroll-form-td--price-list spo-enroll-form-td--dim-end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="spo-enroll-form-price-list spo-enroll-form-dim-list">
                              {skuList.length === 0 ? (
                                <span className="spo-muted">—</span>
                              ) : skuList.map((skuRow, skuIdx) => {
                                const code = String(skuRow?.sku || '').trim();
                                const skuDraft = draft.skus?.[code] || {};
                                const baseRef = referencePriceOf(extractSkuPriceInfoList(skuRow)[0]);
                                return (
                                  <div
                                    key={`${code || 'sku'}-${skuIdx}`}
                                    className="spo-enroll-form-price-line spo-enroll-form-dim-line"
                                  >
                                    {renderPriceInput({
                                      modeKey: `${skc}::${code}`,
                                      priceValue: skuDraft.price,
                                      rateValue: skuDraft.rate,
                                      supplyPrice: baseRef.value,
                                      supplyCurrency: baseRef.currency,
                                      label: code || skc,
                                      fill: true,
                                      onChangePrice: (v) => writePrice(skc, code, 'price', v, baseRef.value),
                                      onChangeRate: (v) => writePrice(skc, code, 'rate', v, baseRef.value),
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        ) : (
                          <td
                            className="spo-enroll-form-td--center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="spo-enroll-form-price-line">
                              {renderPriceInput({
                                modeKey: skc,
                                priceValue: draft.price,
                                rateValue: draft.rate,
                                supplyPrice: referencePriceOf(skcPrices[0]).value,
                                supplyCurrency: referencePriceOf(skcPrices[0]).currency,
                                label: skc,
                                onChangePrice: (v) => writePrice(
                                  skc,
                                  null,
                                  'price',
                                  v,
                                  referencePriceOf(skcPrices[0]).value,
                                ),
                                onChangeRate: (v) => writePrice(
                                  skc,
                                  null,
                                  'rate',
                                  v,
                                  referencePriceOf(skcPrices[0]).value,
                                ),
                              })}
                            </div>
                          </td>
                        )
                      ) : null}
                      <td className="spo-enroll-form-td--rule">
                        <div>{fixedUi('库存')} {row.stock ?? '—'}</div>
                        <div className="spo-muted">
                          {rateHint(skcRules) || rateHint(extractSkuSiteRules(skuList[0])) || '—'}
                        </div>
                        {!isSelfOperated && skcPrices[0]?.salePrice != null ? (
                          <div className="spo-muted">
                            {fixedUi('售价')} {formatPrice(skcPrices[0].salePrice, skcPrices[0].currency)}
                          </div>
                        ) : null}
                      </td>
                      <td
                        className="spo-marketing-enroll-ops spo-enroll-form-td--ops"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="spo-marketing-list__ops">
                          <button
                            type="button"
                            className="spo-btn spo-btn--secondary spo-btn--sm"
                            disabled={pendingEnrollSkcs.has(skc)}
                            onClick={() => requestEnroll([row])}
                          >
                            {pendingEnrollSkcs.has(skc) ? fixedUi('报名中…') : fixedUi('报名')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                });
                return rows;
              })}
            </tbody>
          </table>
        </div>

        <div className="spo-enroll-form__pager">
          <SheinOpsPager
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            ariaLabel={fixedUi('可报商品分页')}
          />
        </div>
      </ProductListCard>

      <BatchQueryModal
        open={skcBatchOpen}
        title={fixedUiTpl('批量查询 {0}', queryField.toUpperCase())}
        initialValue={queryDraft}
        placeholder={fixedUiTpl('每行一个 {0}，或用逗号分隔', queryField.toUpperCase())}
        onConfirm={(tokens) => {
          const applied = { field: queryField, tokens };
          setQueryDraft(tokens.join(','));
          setAppliedQuery(applied);
          if (page === 1) loadProducts(1, pageSize, applied);
          else setPage(1);
        }}
        onClose={() => setSkcBatchOpen(false)}
      />

      <IpeConfirmDialog
        open={Boolean(enrollConfirm)}
        title={t('marketing.enConfirm')}
        message={enrollConfirm
          ? (enrollConfirm.single
            ? t('marketing.enConfirmOne', { name: titleName, skc: skcOf(enrollConfirm.rows[0]) })
            : t('marketing.enConfirmMany', { name: titleName, count: enrollConfirm.rows.length }))
          : ''}
        confirmLabel={t('marketing.enConfirm')}
        onConfirm={confirmEnroll}
        onCancel={() => setEnrollConfirm(null)}
      />
    </div>
  );
}
